import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { styled } from '@mui/material/styles';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import StyleIcon from '@mui/icons-material/Style';
import QuizIcon from '@mui/icons-material/Quiz';
import SummarizeIcon from '@mui/icons-material/Summarize';

const SelectorContainer = styled(Paper)(({ theme }) => ({
  background: '#1a1a2e',
  borderRadius: '16px',
  padding: theme.spacing(3),
  border: '1px solid rgba(102, 126, 234, 0.1)',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
  marginBottom: theme.spacing(3),
}));

const StyledToggleButton = styled(ToggleButton)(({ theme }) => ({
  padding: theme.spacing(1.5, 3),
  borderRadius: '12px',
  textTransform: 'none',
  fontWeight: 600,
  border: '2px solid rgba(102, 126, 234, 0.2)',
  margin: theme.spacing(0, 0.5),
  transition: 'all 0.3s ease',
  backgroundColor: '#1a1a2e',
  color: '#e2e8f0',
  '&:not(.Mui-selected)': {
    backgroundColor: 'rgba(102, 126, 234, 0.05)',
    border: '2px solid rgba(102, 126, 234, 0.1)',
    color: '#cbd5e1',
    '&:hover': {
      backgroundColor: 'rgba(102, 126, 234, 0.1)',
      borderColor: '#667eea',
      color: '#e2e8f0',
    },
  },
  '&.Mui-selected': {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: '2px solid transparent',
    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
    '&:hover': {
      background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
    },
  },
}));

const ModeDescription = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
  marginTop: theme.spacing(1),
  textAlign: 'center',
}));

export default function StudyModeSelector({ mode, onModeChange }) {
  const handleModeChange = (event, newMode) => {
    if (newMode !== null) {
      onModeChange(newMode);
    }
  };

  const getModeDescription = (currentMode) => {
    switch (currentMode) {
      case 'reading':
        return 'Leia o conteúdo completo com ferramentas de estudo';
      case 'flashcards':
        return 'Revise conceitos importantes com cartões de memorização';
      case 'practice':
        return 'Teste seus conhecimentos com questões práticas';
      case 'summary':
        return 'Veja os pontos principais em formato resumido';
      default:
        return '';
    }
  };

  return (
    <SelectorContainer elevation={0}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#f1f5f9' }}>
          Modo de Estudo
        </Typography>
        
        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={handleModeChange}
          aria-label="modo de estudo"
          sx={{ flexWrap: 'wrap', justifyContent: 'center' }}
        >
          <StyledToggleButton value="reading" aria-label="leitura">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AutoStoriesIcon />
              <span>Leitura</span>
            </Box>
          </StyledToggleButton>
          
          <StyledToggleButton value="flashcards" aria-label="flashcards">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <StyleIcon />
              <span>Flashcards</span>
            </Box>
          </StyledToggleButton>
          
          <StyledToggleButton value="practice" aria-label="prática">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <QuizIcon />
              <span>Prática</span>
            </Box>
          </StyledToggleButton>
          
          <StyledToggleButton value="summary" aria-label="resumo">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SummarizeIcon />
              <span>Resumo</span>
            </Box>
          </StyledToggleButton>
        </ToggleButtonGroup>
        
        <ModeDescription>
          {getModeDescription(mode)}
        </ModeDescription>
      </Box>
    </SelectorContainer>
  );
}